import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext, useEffect } from "react";
import z from "zod";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableTicketType,
	useEnableTicketType,
	useUpdateTicketType,
} from "@/services/ticket/ticket.mutations";
import { ticketQueryOptions } from "@/services/ticket/ticket.options";
import { TicketTypeDrawerContext } from "@/utils/ticket-type-detail/ticket-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { OrgTicketTypeFullPageTab } from "./OrgTicketTypeFullPage";
import { OrgTicketTypeFullPage } from "./OrgTicketTypeFullPage";

const FULL_PARENT =
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full" as const;

const SearchSchema = z.object({
	tab: z
		.enum(["details", "access", "form", "lifecycle", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's drawer/full view. Currently unused on form rules. */
	ruleTab: z.enum(["details", "activity"]).optional(),
});

export const Route = createFileRoute(FULL_PARENT)({
	component: OrgTicketTypeFullLayoutRoute,
	validateSearch: (search) => SearchSchema.parse(search),
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; ticketTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				ticketTypeId: z.string().parse(p.ticketTypeId),
			};
		},
	},
});

function OrgTicketTypeFullLayoutRoute() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({ from: FULL_PARENT });
	const search = useSearch({ from: FULL_PARENT });
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const drawerCtx = useContext(TicketTypeDrawerContext);
	const accessEdit = useTypeAccessEditAllowed(
		"TicketType",
		params.ticketTypeId,
	);
	const updateMutation = useUpdateTicketType();
	const enableMutation = useEnableTicketType();
	const disableMutation = useDisableTicketType();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending;
	const organizationIdForApi = params.orgId;

	// Use the full page's own search.mode — not drawerCtx.mode, which reads the
	// drawer route's search params and always defaults to "view" on the full route.
	const isEditMode =
		search.mode === "edit" &&
		(search.tab === "access"
			? accessEdit.allowed
			: (drawerCtx?.canEdit ?? false));

	useEffect(() => {
		if (search.mode !== "edit" || search.tab !== "access") return;
		if (accessEdit.isPending || accessEdit.allowed) return;
		void navigate({
			to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
			params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
			search: {
				tab: search.tab,
				mode: "view",
				presentation: search.presentation,
			},
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		navigate,
		params.orgId,
		params.ticketTypeId,
		search.mode,
		search.presentation,
		search.tab,
	]);

	const detailQuery = useQuery(
		ticketQueryOptions.ticketTypeDetail(params.ticketTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const ticketType = detailQuery.data ?? null;

	const formSegment = `/configuration/ticketTypes/${params.ticketTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/ticketTypes/${params.ticketTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: OrgTicketTypeFullPageTab = onFormRoutes
		? "form"
		: search.tab;

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const handleTabChange = (tab: OrgTicketTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
				params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
			params: { orgId: params.orgId, ticketTypeId: params.ticketTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({
			to: "/org-admin/$orgId/configuration/ticketTypes",
			params: { orgId: params.orgId },
		});
	};

	// Build a full-page-specific context that overrides navigation to stay within
	// the full page route instead of redirecting to the drawer.
	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
						params: {
							orgId: params.orgId,
							ticketTypeId: params.ticketTypeId,
						},
						search: {
							tab: search.tab,
							mode,
							presentation: search.presentation,
						},
					});
				},
				isSaving: isSavingAny,
				saveDraft: async () => {
					if (!drawerCtx.isValid || isSavingAny) return;
					if (
						search.tab === "access"
							? !accessEdit.allowed
							: !drawerCtx.canEdit
					)
						return;
					try {
						if (search.tab === "access") {
							if (drawerCtx.draftSubOrgIds === null) {
								await enableMutation.mutateAsync({
									ticketTypeId: params.ticketTypeId,
									organizationId: organizationIdForApi,
								});
							} else if (drawerCtx.draftSubOrgIds.length === 0) {
								await disableMutation.mutateAsync({
									ticketTypeId: params.ticketTypeId,
									organizationId: organizationIdForApi,
								});
							} else {
								await updateMutation.mutateAsync({
									ticketTypeId: params.ticketTypeId,
									organizationId: organizationIdForApi,
									data: {
										name: drawerCtx.ticketType.name?.trim() ?? "",
										description: drawerCtx.ticketType.description || null,
										icon: drawerCtx.ticketType.icon || null,
										typeId: params.ticketTypeId,
										statusLifecycleId: drawerCtx.ticketType.lifecycleId ?? null,
										subOrganizationIds: drawerCtx.draftSubOrgIds,
										locationRequired: drawerCtx.locationRequired,
									},
								});
							}
							showSuccessToast({
								message: t("admin.ticketTypes.drawer.access.toast.saveSuccess"),
							});
						} else {
							const currentSubOrgIds = (
								ticketType?.ticketTypeSubOrganizations ?? []
							)
								.map((s) => s.id)
								.filter((id): id is string => id !== null);
							await updateMutation.mutateAsync({
								ticketTypeId: params.ticketTypeId,
								organizationId: organizationIdForApi,
								data: {
									name: drawerCtx.draftName.trim(),
									description: drawerCtx.draftDescription || null,
									icon: drawerCtx.draftIcon || null,
									typeId: params.ticketTypeId,
									statusLifecycleId: drawerCtx.ticketType.lifecycleId ?? null,
									subOrganizationIds:
										currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
									locationRequired: drawerCtx.locationRequired,
								},
							});
							showSuccessToast({
								message: t(
									"admin.ticketTypes.drawer.details.toast.saveSuccess",
								),
							});
						}
						void navigate({
							to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
							params: {
								orgId: params.orgId,
								ticketTypeId: params.ticketTypeId,
							},
							search: {
								tab: search.tab,
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler in root-provider.tsx shows the toast
					}
				},
				cancelDraft: () => {
					drawerCtx.setDraftName(drawerCtx.ticketType.name ?? "");
					drawerCtx.setDraftDescription(drawerCtx.ticketType.description ?? "");
					drawerCtx.setDraftIcon(drawerCtx.ticketType.icon ?? "");
					if (ticketType) {
						const subOrgIds = (ticketType.ticketTypeSubOrganizations ?? [])
							.map((s) => s.id)
							.filter((id): id is string => id !== null);
						drawerCtx.setDraftSubOrgIds(
							subOrgIds.length > 0 ? subOrgIds : null,
						);
					}
					void navigate({
						to: "/org-admin/$orgId/configuration/ticketTypes/$ticketTypeId/full",
						params: {
							orgId: params.orgId,
							ticketTypeId: params.ticketTypeId,
						},
						search: {
							tab: search.tab,
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	if (!ticketType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!ticketType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.ticketTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	return (
		<TicketTypeDrawerContext.Provider value={fullPageContext}>
			<OrgTicketTypeFullPage
				ticketType={ticketType}
				organizationId={params.orgId}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</OrgTicketTypeFullPage>
		</TicketTypeDrawerContext.Provider>
	);
}
